import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FaCheckCircle, FaGift } from "react-icons/fa";
import "./WishlistPage.css";

const normalizeName = (value) => String(value || "").trim().toLowerCase();

const WishlistPage = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionItemId, setActionItemId] = useState(null);
  const [buyerName, setBuyerName] = useState("");
  const [rsvpCode, setRsvpCode] = useState("");
  const [codeNames, setCodeNames] = useState([]);
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [showMineOnly, setShowMineOnly] = useState(false);

  const buyerNameNormalized = useMemo(() => normalizeName(buyerName), [buyerName]);

  const loadWishlistItems = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch("/api/wishlist");
      if (!response.ok) {
        throw new Error("Failed to load wishlist");
      }
      const data = await response.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading wishlist:", err);
      setError("Kunne ikke laste ønskelisten. Prøv igjen.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const codeFromUrl = (params.get("code") || "").trim();
    setRsvpCode(codeFromUrl);

    if (codeFromUrl) {
      fetch(`/api/rsvp-code/${codeFromUrl}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!data || !Array.isArray(data.names)) return;
          setCodeNames(data.names);
          if (data.names.length > 0) {
            setBuyerName(data.names[0]);
          }
        })
        .catch((err) => {
          console.error("Could not load RSVP code names:", err);
        });
    }

    loadWishlistItems();
  }, []);

  const toggleBought = async (itemId) => {
    if (!buyerName.trim()) {
      alert("Skriv inn navnet ditt for å markere gave som kjøpt.");
      return;
    }

    setActionItemId(itemId);
    try {
      const response = await fetch(`/api/wishlist/${itemId}/toggle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: buyerName.trim(),
          code: rsvpCode || null,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to update item");
      }

      setItems((prevItems) =>
        prevItems.map((item) =>
          item.id === itemId ? { ...item, ...result.item } : item
        )
      );
    } catch (err) {
      alert(err.message || "Kunne ikke oppdatere gave.");
    } finally {
      setActionItemId(null);
    }
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (showAvailableOnly && item.isBought) {
        return false;
      }

      if (showMineOnly) {
        if (!buyerNameNormalized) {
          return false;
        }
        return (
          item.isBought && normalizeName(item.boughtBy) === buyerNameNormalized
        );
      }

      return true;
    });
  }, [items, showAvailableOnly, showMineOnly, buyerNameNormalized]);

  return (
    <div className="wishlist-page">
      <header className="wishlist-header">
        <div className="container wishlist-header-inner">
          <h2>Lars & Guro</h2>
          <a href="/" className="wishlist-back-link">
            Tilbake til hovedsiden
          </a>
        </div>
      </header>

      <main className="wishlist-main section">
        <div className="container">
          <motion.h1
            className="section-title"
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Ønskeliste
          </motion.h1>

          <p className="wishlist-money-note">
            Vi setter også stor pris på penger som bryllupsgave
          </p>

          <div className="wishlist-name-box">
            <label htmlFor="wishlist-buyer-name">Ditt navn</label>
            <input
              id="wishlist-buyer-name"
              type="text"
              list="wishlist-name-options"
              value={buyerName}
              onChange={(event) => setBuyerName(event.target.value)}
              placeholder="Skriv navnet ditt"
            />
            {codeNames.length > 0 && (
              <datalist id="wishlist-name-options">
                {codeNames.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            )}
            {rsvpCode && (
              <p className="wishlist-hint">Kode registrert: {rsvpCode}</p>
            )}

            <div className="wishlist-filter-row">
              <button
                type="button"
                className={`wishlist-filter-btn ${showAvailableOnly ? "active" : ""}`}
                onClick={() => {
                  setShowAvailableOnly((prev) => {
                    const next = !prev;
                    if (next) {
                      setShowMineOnly(false);
                    }
                    return next;
                  });
                }}
              >
                Kun ledige gaver
              </button>
              <button
                type="button"
                className={`wishlist-filter-btn ${showMineOnly ? "active" : ""}`}
                onClick={() => {
                  setShowMineOnly((prev) => {
                    const next = !prev;
                    if (next) {
                      setShowAvailableOnly(false);
                    }
                    return next;
                  });
                }}
              >
                Mine markerte gaver
              </button>
            </div>
          </div>

          {isLoading ? (
            <p className="wishlist-feedback">Laster ønskeliste...</p>
          ) : error ? (
            <p className="wishlist-feedback wishlist-error">{error}</p>
          ) : items.length === 0 ? (
            <p className="wishlist-feedback">Ingen ønsker er lagt inn ennå.</p>
          ) : filteredItems.length === 0 ? (
            <p className="wishlist-feedback">Ingen gaver matcher filteret.</p>
          ) : (
            <div className="wishlist-grid">
              {filteredItems.map((item, index) => {
                const isBoughtByCurrentUser =
                  item.isBought &&
                  normalizeName(item.boughtBy) === buyerNameNormalized;

                const isBoughtByOtherUser =
                  item.isBought && !isBoughtByCurrentUser;

                return (
                  <motion.article
                    key={item.id}
                    className={`wishlist-card ${item.isBought ? "is-bought" : ""}`}
                    initial={{ opacity: 0, y: 25 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <a href={item.url} target="_blank" rel="noreferrer" className="wishlist-image-link">
                      <img src={item.imageUrl} alt={item.title} className="wishlist-image" />
                    </a>

                    <div className="wishlist-card-content">
                      <a href={item.url} target="_blank" rel="noreferrer" className="wishlist-title-link">
                        <h3>{item.title}</h3>
                      </a>

                      <p className="wishlist-quantity">Antall: {item.quantity || 1}</p>

                      {item.isBought ? (
                        <p className="wishlist-status">
                          <FaCheckCircle /> Markert som kjøpt
                        </p>
                      ) : (
                        <p className="wishlist-status">
                          <FaGift /> Ledig
                        </p>
                      )}

                      <button
                        type="button"
                        className="wishlist-toggle-btn"
                        onClick={() => toggleBought(item.id)}
                        disabled={
                          actionItemId === item.id ||
                          !buyerName.trim() ||
                          isBoughtByOtherUser
                        }
                      >
                        {actionItemId === item.id
                          ? "Oppdaterer..."
                          : isBoughtByCurrentUser
                            ? "Marker som ikke kjøpt"
                            : isBoughtByOtherUser
                              ? "Allerede markert"
                              : "Marker som kjøpt"}
                      </button>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default WishlistPage;
